import { useCallback, useEffect, useState } from 'react';
import {
  type Countdown,
  pauseCountdown,
  resumeCountdown,
  secondsLeft,
  startCountdown,
} from '../domain/countdown';

/**
 * The exercise countdown (DESIGN: the orange timer chip + its sheet), shared by web and
 * mobile. It keeps the moment the countdown ends rather than ticking a number down, so time
 * spent in another app — reading a book in Kindle, say — still counts when the person comes
 * back; the once-a-second tick only refreshes what the screen shows.
 */
export function useExerciseTimer(totalSeconds: number) {
  const [countdown, setCountdown] = useState<Countdown>(() =>
    startCountdown(totalSeconds, Date.now()),
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!countdown.running) return;
    const tick = () => setNow(Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [countdown]);

  const pause = useCallback(
    () => setCountdown((current) => pauseCountdown(current, Date.now())),
    [],
  );
  const resume = useCallback(
    () => setCountdown((current) => resumeCountdown(current, Date.now())),
    [],
  );
  const reset = useCallback(() => {
    const at = Date.now();
    setNow(at);
    setCountdown(startCountdown(totalSeconds, at));
  }, [totalSeconds]);

  return { seconds: secondsLeft(countdown, now), running: countdown.running, pause, resume, reset };
}
